import { notification } from "antd";
import { get } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCheckCircle } from "react-icons/md";
import { useLazyQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { PROJECTS_LIST } from "utils/api/graphql/queries/projects";
import { CONTACT_POINT_SYSTEM } from "utils/constants";
import InfoBox from "../InfoBox";
import View from "./View";

const DuplicateCheck = ({ contact, onClick }) => {
  const take = 10;
  const { t } = useTranslation(NAME_SPACES.ERRORS);
  const API_ERRORS = t("API", { returnObjects: true });
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();
  const abortControllerRef = useRef();
  const requestIdRef = useRef(0);

  const filter = useMemo(
    () => ({
      advancedFilters: [
        {
          field: "contact.user.lastname",
          operator: "contains",
          value: get(contact, "user.lastname", ""),
          value2: null,
          values: null,
        },
        {
          field: "contact.user.firstname",
          operator: "contains",
          value: get(contact, "user.firstname", ""),
          value2: null,
          values: null,
        },
        {
          field: "contact.telecoms.email",
          operator: "contains",
          value: get(
            contact,
            `telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
            "",
          ),
          value2: null,
          values: null,
        },
        {
          field: "contact.telecoms.phone",
          operator: "contains",
          value: get(
            contact,
            `telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
            "",
          ),
          value2: null,
          values: null,
        },
      ].filter((f) => f.value),
      take,
    }),
    [contact],
  );

  const variables = useMemo(
    () => ({
      ...filter,
      withSubscriptions: false,
      withContract: false,
      withProcess: false,
      withProcessStatuses: false,
      withTracers: false,
      withFlows: false,
      exclude: [
        "subscriptions",
        "contract",
        "process",
        "processStatuses",
        "flows",
        "tracers",
        "possibleStatuses",
        "conditions",
        "actions",
        "actionsConditions",
      ],
    }),
    [filter],
  );

  const [fetchProjects] = useLazyQuery(PROJECTS_LIST, {
    onError: (error) => {
      if (error?.networkError?.name === "AbortError") return;
      notification.error({ message: API_ERRORS.GENERIC });
    },
  });

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setLoading(true);
    debounceRef.current = setTimeout(() => {
      fetchProjects({
        variables,
        context: {
          fetchOptions: {
            signal: abortController.signal,
          },
        },
      })
        .then((response) => {
          if (requestId !== requestIdRef.current) return;
          setData(response?.data);
        })
        .catch((error) => {
          if (error?.networkError?.name === "AbortError") return;
          if (requestId === requestIdRef.current) setData(undefined);
        })
        .finally(() => {
          if (requestId === requestIdRef.current) setLoading(false);
        });
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortController.abort();
    };
  }, [fetchProjects, variables]);

  if (loading) return;
  if (!get(data, "projectsList.count", 0))
    return (
      <InfoBox
        icon={<MdCheckCircle size={24} />}
        content={
          <div onClick={() => {}}>
            <h3>Aucun doublon détecté</h3>
            <p>Vous pouvez créer le projet en toute sécurité.</p>
          </div>
        }
        variant="success"
      />
    );
  return (
    <View
      onClick={onClick}
      count={get(data, "projectsList.count", 0)}
      take={Math.min(take, get(data, "projectsList.count", 0))}
      projects={omitDeep(get(data, "projectsList.data", []), "__typename")}
    />
  );
};

export default DuplicateCheck;
